export const IMOVEL_FORM_OWNER = Object.freeze({
  WIZARD: 'criar_imovel_wizard_simplificado',
  EDITOR: 'imovel_form_editor_completo',
});

export const IMOVEL_FORM_OWNER_CONTRACT = Object.freeze({
  canonicalCreationOwner: IMOVEL_FORM_OWNER.WIZARD,
  canonicalEditorOwner: IMOVEL_FORM_OWNER.EDITOR,
  wizardLabel: 'Criar imóvel',
  wizardRole: 'Wizard simplificado para criação rápida e publicação inicial.',
  editorLabel: 'Editor completo',
  editorRole:
    'Owner canônico de edição completa, estimativa, taxas, acessos e chaves.',
  handoffTarget: IMOVEL_FORM_OWNER.EDITOR,
  operationalSections: [
    'estimativa_qpreco',
    'tax_fee_governance',
    'entry_access_keys',
  ],
});

const OWNER_LABELS = Object.freeze({
  [IMOVEL_FORM_OWNER.WIZARD]: IMOVEL_FORM_OWNER_CONTRACT.wizardLabel,
  [IMOVEL_FORM_OWNER.EDITOR]: IMOVEL_FORM_OWNER_CONTRACT.editorLabel,
});

export const cleanObject = object =>
  Object.fromEntries(
    Object.entries(object || {}).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    })
  );

export const cleanDeepObject = object =>
  Object.fromEntries(
    Object.entries(object || {})
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.filter(Boolean)];
        }

        if (value && typeof value === 'object') {
          return [key, cleanDeepObject(value)];
        }

        return [key, value];
      })
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') {
          return Object.keys(value).length > 0;
        }
        return value !== '' && value !== null && value !== undefined;
      })
  );

export const normalizeOptionalText = value =>
  typeof value === 'string' ? value.trim() || null : value;

const normalizeRequiredText = value =>
  typeof value === 'string' ? value.trim() : value || '';

export const normalizeOptionalNumber = value => {
  if (value === '' || value === null || value === undefined) return null;

  const rawValue = String(value).trim();
  let normalizedValue = rawValue.replace(',', '.');
  if (rawValue.includes('.') && rawValue.includes(',')) {
    normalizedValue = rawValue.replace(/\./g, '').replace(',', '.');
  } else if (rawValue.match(/^\d{1,3}(\.\d{3})+$/)) {
    normalizedValue = rawValue.replace(/\./g, '');
  }
  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeOptionalInteger = value => {
  if (value === '' || value === null || value === undefined) return null;

  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCep = value => {
  if (value === '' || value === null || value === undefined) return null;

  const normalized = String(value).replace(/\D/g, '');
  return normalized || normalizeOptionalText(value);
};

const normalizeMidias = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => (typeof item === 'string' ? item : item?.url))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [];
};

export const normalizeImovelAdditionalAttributes = (
  additionalAttributes = {},
  { owner = IMOVEL_FORM_OWNER.EDITOR, mode = 'edit' } = {}
) =>
  cleanDeepObject({
    ...additionalAttributes,
    canonical_form_owner: owner,
    canonical_form_owner_label: OWNER_LABELS[owner],
    canonical_flow_mode: mode,
    canonical_creation_owner: IMOVEL_FORM_OWNER_CONTRACT.canonicalCreationOwner,
    canonical_editor_owner: IMOVEL_FORM_OWNER_CONTRACT.canonicalEditorOwner,
    canonical_handoff_target: IMOVEL_FORM_OWNER_CONTRACT.handoffTarget,
    form_owner_contract: {
      owner_decision:
        'Criar imóvel é o wizard simplificado de criação; ImovelForm é o editor completo canônico.',
      wizard_owner: IMOVEL_FORM_OWNER_CONTRACT.canonicalCreationOwner,
      wizard_role: IMOVEL_FORM_OWNER_CONTRACT.wizardRole,
      editor_owner: IMOVEL_FORM_OWNER_CONTRACT.canonicalEditorOwner,
      editor_role: IMOVEL_FORM_OWNER_CONTRACT.editorRole,
      handoff_target: IMOVEL_FORM_OWNER_CONTRACT.handoffTarget,
      operational_sections: IMOVEL_FORM_OWNER_CONTRACT.operationalSections,
    },
  });

export const normalizeImovelPayload = (
  payload = {},
  { owner = IMOVEL_FORM_OWNER.EDITOR, mode = 'edit' } = {}
) => ({
  ...payload,
  external_id: normalizeOptionalText(payload.external_id),
  titulo: normalizeRequiredText(payload.titulo),
  descricao: normalizeRequiredText(payload.descricao),
  tipo: normalizeRequiredText(payload.tipo || 'apartamento'),
  finalidade: normalizeRequiredText(payload.finalidade || 'venda'),
  status: normalizeRequiredText(payload.status || 'disponivel'),
  valor_venda: normalizeOptionalNumber(payload.valor_venda),
  valor_aluguel: normalizeOptionalNumber(payload.valor_aluguel),
  valor_iptu: normalizeOptionalNumber(payload.valor_iptu),
  valor_condominio: normalizeOptionalNumber(payload.valor_condominio),
  quartos: normalizeOptionalInteger(payload.quartos),
  banheiros: normalizeOptionalInteger(payload.banheiros),
  vagas_garagem: normalizeOptionalInteger(payload.vagas_garagem),
  area_util: normalizeOptionalNumber(payload.area_util),
  area_total: normalizeOptionalNumber(payload.area_total),
  endereco: normalizeRequiredText(payload.endereco),
  bairro: normalizeRequiredText(payload.bairro),
  cidade: normalizeRequiredText(payload.cidade),
  uf: normalizeRequiredText(payload.uf),
  cep: normalizeCep(payload.cep),
  latitude: normalizeOptionalNumber(payload.latitude),
  longitude: normalizeOptionalNumber(payload.longitude),
  portal_origem: normalizeOptionalText(payload.portal_origem),
  link_externo: normalizeOptionalText(payload.link_externo),
  midias: normalizeMidias(payload.midias),
  caracteristicas: cleanObject(payload.caracteristicas || {}),
  additional_attributes: normalizeImovelAdditionalAttributes(
    payload.additional_attributes,
    { owner, mode }
  ),
});
